import { attached } from "./notes";

export default {
  // Boards hidden for this presentation are skipped when paging; the
  // overview still shows them, dimmed, with Show under them to bring them
  // back (overview.js).
  hiddenAt(index) {
    return this.slideTargets[index]?.dataset.hidden === "true";
  },

  visibleIndices() {
    return this.slideTargets.map((_, i) => i).filter((i) => !this.hiddenAt(i));
  },

  // The board the room sees in place of `index`: itself, or the next board
  // on show after it, or the last before it. End, a link, a dimmed board
  // picked in the overview and a page turn from another window each reached
  // a hidden board, which counted as "01 / N" with paging dead.
  nearestVisible(index) {
    if (!this.hiddenAt(index)) return index;
    const visible = this.visibleIndices();
    return visible.find((i) => i > index) ?? visible.filter((i) => i < index).pop() ?? index;
  },

  // Hide or show a board for this presentation. Saved on the report, so
  // the boss's share link and the PDF hide it too.
  async toggleHidden(event) {
    event.stopPropagation();
    const slug = event.currentTarget.dataset.slug;
    const board = this.slideTargets.find((s) => s.dataset.slideSlug === slug);
    if (!board) return;
    const hiding = board.dataset.hidden !== "true";
    const hidden = new Set(this.slideTargets.filter((s) => s.dataset.hidden === "true" && !attached(s)).map((s) => s.dataset.slideSlug));
    if (hiding) hidden.add(slug);
    else hidden.delete(slug);

    const url = this.element.dataset.presentationSettingsUrl;
    if (!url) return;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ hidden_boards: [...hidden] }),
    });
    if (!response.ok) return;

    this.slideTargets.forEach((s) => {
      if (s.dataset.slideSlug !== slug) return;
      if (hiding) s.dataset.hidden = "true";
      else delete s.dataset.hidden;
    });
    this.syncOpeners();
    this.renderTiles();
    this.showSlide(this.indexValue, false, false);
  },

  // A chapter's opening page goes with the last of its boards, and a
  // board's front page with its board.
  syncOpeners() {
    this.slideTargets.filter((board) => "opener" in board.dataset).forEach((opener) => {
      const shown = this.slideTargets.some((board) =>
        !attached(board) && board.dataset.chapter === opener.dataset.chapter && board.dataset.hidden !== "true");
      if (shown) delete opener.dataset.hidden;
      else opener.dataset.hidden = "true";
    });
    this.slideTargets.filter((board) => "front" in board.dataset).forEach((front) => {
      const board = this.slideTargets.find((other) => other.dataset.slideSlug === front.dataset.front);
      if (board && board.dataset.hidden !== "true") delete front.dataset.hidden;
      else front.dataset.hidden = "true";
    });
  },

  // The boards hidden now, on a deck already drawn. The board on show, if
  // it has gone, gives way to the next one the room will see.
  applyHidden(hidden) {
    const gone = new Set(hidden);
    this.slideTargets.forEach((board) => {
      if (gone.has(board.dataset.slideSlug)) board.dataset.hidden = "true";
      else delete board.dataset.hidden;
    });
    this.syncOpeners();
    this.renderTiles();
    this.showSlide(this.nearestVisible(this.indexValue), true, false);
  },
};
